import { useLayoutEffect, useRef } from "react";
import type { WebsiteConfig } from "../types/core";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { useWBConsent } from "../consent/WBConsentProvider";
import { resolveWbChrome } from "../locale/wbChrome";
import { resolveBodyColor } from "../theme/themeValueResolver";

// Legal line of every published site: legal notice · privacy · report content.
//
// Mounted by App.tsx at RUNTIME level, next to the cookie banner — not inside
// the operator's footer node. The footer is optional, deletable and can be
// hidden by a visibility rule; this line has no off state, because the site is
// hosted by Veltric whatever the operator builds. The operator decides only
// WHOSE pages the first two links open (WebsiteConfig.imprintUrl /
// .privacyPolicyUrl); "report content" is the platform's DSA Art. 16 channel
// and is never theirs to point elsewhere.
//
// A <nav>, deliberately not a <footer>: the operator's footer block renders a
// <footer>, and a page with two is what strict e2e locators (and screen-reader
// landmark lists) trip over.

export const PLATFORM_IMPRINT_URL = "https://veltric.app/impressum";
export const PLATFORM_PRIVACY_URL = "https://veltric.app/datenschutz";
export const DSA_REPORT_ADDRESS = "dsa@veltric.app";

/**
 * The operator's URL if it is a usable ABSOLUTE http(s) URL, else the
 * platform's. Stricter than `safeHref` on purpose: that one lets relative
 * paths and mailto: through, and a legal link that resolves against the
 * visitor's current path — or opens a mail client — is not a legal page.
 */
export function legalHref(candidate: unknown, fallback: string): string {
  if (typeof candidate !== "string") return fallback;
  const trimmed = candidate.trim();
  if (trimmed === "" || trimmed.length > 2048) return fallback;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return fallback;
    if (parsed.username !== "" || parsed.password !== "") return fallback;
    return parsed.toString();
  } catch {
    return fallback;
  }
}

/** Origin + path only: the query of a guest's login link carries credentials,
 *  and this string ends up in a mail body. */
export function reportContentHref(subject: string, location: Pick<Location, "origin" | "pathname">): string {
  const page = `${location.origin}${location.pathname}`;
  return `mailto:${DSA_REPORT_ADDRESS}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(page)}`;
}

type LegalLineViewProps = {
  lang: string | null | undefined;
  primaryLanguage: string | null | undefined;
  config?: Pick<WebsiteConfig, "imprintUrl" | "privacyPolicyUrl" | "backgroundColor" | "bodyFontColor"> | null;
  /** The fixed language switcher (bottom-right, ~50px band) is on screen. */
  clearLanguageSwitcher?: boolean;
  /**
   * Art. 7(3) GDPR: withdrawing consent must be as easy as giving it. Only the
   * mounted line passes this — the error shells render without a consent
   * provider, and there is nothing to withdraw on a page that loaded nothing.
   */
  onOpenConsentSettings?: (() => void) | undefined;
};

/** Context-free half — also used by App.tsx's error shells, which render
 *  before (or instead of) the theme and language providers. */
export function LegalLineView({
  lang,
  primaryLanguage,
  config,
  clearLanguageSwitcher = false,
  onOpenConsentSettings,
}: LegalLineViewProps) {
  const chrome = resolveWbChrome(lang, primaryLanguage);
  // py-1: wrapped rows stay ≥24px tall targets (WCAG 2.5.8).
  const linkClass = "underline underline-offset-2 whitespace-nowrap py-1";
  // Same resolver as the body copy above it, fed from the config because the
  // error shells have no theme provider: older sites store no bodyFontColor,
  // and a fixed grey (#5d5d5d) is ~2.9:1 on a dark page. See inkLegibility.ts.
  const backgroundColor = config?.backgroundColor?.trim() || "#ffffff";
  const color = resolveBodyColor({ override: config?.bodyFontColor, palette: { background: backgroundColor } });
  return (
    <nav
      // Names what the landmark holds, not just its first link.
      aria-label={[chrome.imprint, chrome.privacy, onOpenConsentSettings ? chrome.cookieSettings : null]
        .filter(Boolean)
        .join(" · ")}
      data-testid="wb-legal-line"
      // The switcher is `fixed bottom-4 right-4` and a good 100px wide: side
      // padding cannot clear it on a phone (the right-hand link ends up under
      // the select, unclickable). Bottom padding lifts the whole row above
      // its band instead.
      className={`flex flex-wrap justify-center gap-x-4 gap-y-0 w-full px-4 pt-2 text-xs ${
        clearLanguageSwitcher ? "pb-16" : "pb-2"
      }`}
      style={{ backgroundColor, color }}
    >
      <a
        href={legalHref(config?.imprintUrl, PLATFORM_IMPRINT_URL)}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
        data-testid="wb-legal-imprint"
      >
        {chrome.imprint}
      </a>
      <a
        href={legalHref(config?.privacyPolicyUrl, PLATFORM_PRIVACY_URL)}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
        data-testid="wb-legal-privacy"
      >
        {chrome.privacy}
      </a>
      <a
        href={reportContentHref(chrome.reportContent, window.location)}
        className={linkClass}
        data-testid="wb-legal-report"
      >
        {chrome.reportContent}
      </a>
      {onOpenConsentSettings !== undefined && (
        // A button, not a link: it changes state on this page, it goes nowhere.
        <button
          type="button"
          onClick={onOpenConsentSettings}
          className={`${linkClass} cursor-pointer bg-transparent border-0 p-0 m-0`}
          style={{ color: "inherit", font: "inherit" }}
          data-testid="wb-legal-consent"
        >
          {chrome.cookieSettings}
        </button>
      )}
    </nav>
  );
}

/** CSS variable the page wrapper subtracts from its 100dvh floor. */
export const LEGAL_LINE_HEIGHT_VAR = "--wb-legal-line-h";

export function LegalLine({ websiteConfig }: { websiteConfig: WebsiteConfig }) {
  const { currentLang, primaryLanguage, availableLanguages } = useWBLang();
  const box = useRef<HTMLDivElement>(null);
  const consent = useWBConsent();

  // Publish the row's real height (it wraps on narrow screens and changes
  // with the language), so page + line together fill exactly one viewport.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty(LEGAL_LINE_HEIGHT_VAR, `${el.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(LEGAL_LINE_HEIGHT_VAR);
    };
  }, []);

  return (
    <div ref={box}>
      <LegalLineView
        lang={currentLang}
        primaryLanguage={primaryLanguage}
        config={websiteConfig}
        clearLanguageSwitcher={availableLanguages.length > 1}
        // Banner off ⇒ a withdrawal re-opens nothing; with no grant to take
        // back either, the click would change nothing on the page. The
        // placeholders' own buttons stay the consent path (see ConsentGate).
        onOpenConsentSettings={
          websiteConfig.cookieEnabled !== false || consent.canWithdraw ? consent.withdraw : undefined
        }
      />
    </div>
  );
}
